import {
  GetUserSettingsReponse,
  UserData,
  UserDataFullEntity,
  UserPrivileges,
  UserCredits,
  EditableUserData,
  PatchUserDataResponse,
  GetUserEmailAddressesResponse,
} from '../../types/current_user.types';
import { checkStatus } from '../../utilities/fetch';
import {
  currentUserSessionAuthPath,
  settingsPath,
  currentUserPrivilegesPath,
  currentUserCreditsPath,
  consultantsPath,
} from '../../utilities/api_relative_paths';
import { excludeUserFromTracking } from '../../utilities/analytics';
import { createQueryString } from '../../utilities/utilities';

const getUserSettings = (): Promise<GetUserSettingsReponse> =>
  fetch(settingsPath)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));

const getUserEmailAddresses = (userId: number): Promise<GetUserEmailAddressesResponse> => {
  if (!userId) return Promise.reject('No user ID provided');

  return fetch(`${consultantsPath}/${userId}/email_addresses`)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

const updateUserData = (
  editedData: EditableUserData,
  userId: number
): Promise<PatchUserDataResponse> => {
  if (!userId) return Promise.reject('No user ID provided');

  const options = {
    method: 'PATCH',
    body: JSON.stringify({
      consultant: editedData,
    }),
    headers: { 'Content-Type': 'application/json' },
  };

  return fetch(`${consultantsPath}/${userId}`, options)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

const getUserData = (detailLevel?: 'full'): Promise<UserData | UserDataFullEntity> => {
  const urlPath = `${currentUserSessionAuthPath}${
    detailLevel === 'full' ? createQueryString({ detail_level: 'full' }) : ''
  }`;

  return fetch(urlPath)
    .then((response) => {
      // 401 will be returned when user is logged out (no cookies as part of request)
      // When user is logged out from device but logged in another device, it can happen
      // that the BE returns a 403.
      // In both cases, we want to catch those 4** statues in a catch block.
      if ([401, 403].includes(response.status)) {
        return Promise.reject({ status: response.status });
      }
      return response.json();
    })
    .then((response) => {
      if (response.company_name === 'Devex') {
        excludeUserFromTracking();
      }

      return response;
    })
    .catch((error) => Promise.reject(error));
};

const getUserPrivileges = (): Promise<UserPrivileges> =>
  fetch(currentUserPrivilegesPath)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));

const getUserCredits = (privilege: string): Promise<UserCredits> =>
  fetch(`${currentUserCreditsPath}/${privilege}`)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));

export {
  getUserSettings,
  getUserData,
  getUserPrivileges,
  getUserCredits,
  updateUserData,
  getUserEmailAddresses,
};
